import { formatRate, normalizeRateUnit, type RateUnit } from '@/core/display-settings'
import { widgetCopy } from '@/i18n/widget-copy'
import { deriveExperienceInsightsFromValues } from '@/core/live-insights'
import type { PipData } from '@/services/pip-service'
import type { AppLocale } from '@/i18n/messages'
import { normalizePipSkin, normalizePipStreamOptions, type PipSkin, type PipStreamOptions } from './pip-skins'
import type { StreamHudData } from './stream-hud'
import { compact, duration, estimate, liveRate, percent, signedCompact, statusText } from './widget-format'
import { formatExpRateTrend } from './exp-rate-trend'
import { presentExpReadout } from './exp-readout-presentation'
import { selectExperienceReadout } from './experience-readout'

/** Shared display values for everyday and stream skins in the PiP window. */
export interface WidgetSnapshot {
  version: 1
  rateUnit?: RateUnit
  skin: PipSkin
  options: PipStreamOptions
  locale: AppLocale
  theme: 'light' | 'dark'
  data: StreamHudData
  mesosNetTone: -1 | 0 | 1
  mesosBalanceTone: -1 | 0 | 1
  unresolvedExp: boolean
}

export function buildWidgetSnapshot(input: PipData, locale: AppLocale, theme: 'light' | 'dark'): WidgetSnapshot {
  const { session, state, liveRates, isStarting, isEnding } = input
  const rateUnit = normalizeRateUnit(input.rateUnit)
  const ratio = (value: number | null | undefined) => typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0
  const tone = (value: number | null | undefined): -1 | 0 | 1 => !value || !Number.isFinite(value) ? 0 : value > 0 ? 1 : -1
  const selected = selectExperienceReadout(liveRates, session, state, input.levelEtaMode)
  const { value: rate, held, hidden, pending: unresolvedExp } = selected
  const experience = deriveExperienceInsightsFromValues(selected.point?.requiredExp, selected.point?.absolute, rate, selected.etaRate)
  const status = isEnding ? 'ending' : isStarting ? 'starting' : session?.status ?? 'idle'
  const map = state?.mapName ?? widgetCopy(locale, 'Map', '地图')
  return {
    version: 1, rateUnit, skin: normalizePipSkin(input.skin), options: normalizePipStreamOptions(input.streamOptions), locale, theme,
    mesosNetTone: tone(session?.aggregate.mesosDelta), mesosBalanceTone: tone(session?.aggregate.mesosBalanceChange), unresolvedExp,
    data: {
      sessionId: session?.id ?? null, status, rateUnit,
      expChart: liveRates?.expChart,
      statusText: isEnding ? (widgetCopy(locale, 'Finishing', '正在结束')) : isStarting ? (widgetCopy(locale, 'Confirming', '确认中')) : statusText(session?.status, locale),
      elapsed: duration(session?.activeDurationMs ?? 0), level: selected.level == null ? '--' : String(selected.level),
      jobName: selected.level == null ? undefined : state?.jobName?.trim() || undefined,
      expRate: formatRate(rate, locale, rateUnit, { maximumFractionDigits: 1 }),
      expReadout: presentExpReadout(liveRates, session, locale, rateUnit),
      expDisplayRateValue: rate,
      expRateValue: held || hidden ? null : rate,
      expRateTrend: formatExpRateTrend(liveRates?.expRealtime, liveRates?.expPerHour.value, locale, status === 'running' && selected.allowTrend, rateUnit, 1),
      expPercent: percent(selected.point?.ratio, 2), expRatio: ratio(selected.point?.ratio), eta: liveRates?.expReadout && hidden ? '--' : estimate(experience.etaMs, locale),
      expGained: signedCompact(selected.netGain, locale),
      expGainedLabel: (session?.sessionAccountingVersion ?? 9) >= 9 ? (widgetCopy(locale, 'Net EXP', '净')) : (widgetCopy(locale, 'Gained', '本次')),
      fullDuration: estimate(experience.fullLevelDurationMs, locale),
      mesosRate: liveRate(liveRates?.mesosPerHour, locale, rateUnit), mesosNet: signedCompact(session?.aggregate.mesosDelta, locale),
      mesosStart: compact(session?.aggregate.mesosStartBalance, locale), mesosBalanceChange: signedCompact(session?.aggregate.mesosBalanceChange, locale),
      hpPercent: percent(state?.hp.ratio), hpRatio: ratio(state?.hp.ratio),
      mpPercent: percent(state?.mp.ratio), mpRatio: ratio(state?.mp.ratio),
      map: input.enabledMetrics?.mapName === false ? '' : map,
      enabled: { ...(input.enabledMetrics ?? { mesos: true, hp: true, mp: true, mapName: true }) },
      ...(input.levelUpId ? { levelUpId: input.levelUpId } : {}),
    },
  }
}
